import { Button, Divider, Flex, Group, Modal, Select, TextInput } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { StepProps } from 'src/common/ModalFlow';
import { ApiError } from 'src/error/ApiError';
import { z } from 'zod';
import { GenericErrorModal } from '../error/GenericErrorModal';
import PageLoader from '../UI/loader/PageLoader';
import { useClientsContext } from './context/ClientsContext';
import { useAdvisors } from './hooks/useAdvisors';
import { InProgressFamily } from './CreateFamily/dataHelpers';
import { useEffect } from 'react';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { formatName } from 'src/utils/nameFormatting';

const formSchema = z.object({
  name: z.string().min(1),
  advisorUserId: z.string(),
});

export default function FamilyDetailsModal({ close, data, nextStep }: StepProps<InProgressFamily>) {
  const { currentUser, isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { advisors, isLoading: isAdvisorsLoading } = useAdvisors();
  const { error, isLoading } = useClientsContext();

  const initialValues = {
    name: data?.name ?? '',
    advisorUserId: data?.advisorUserId ?? '',
  };

  const form = useForm({
    initialValues,
    validate: schemaResolver(formSchema, { sync: true }),
  });

  useEffect(() => {
    form.setValues(initialValues);
  }, [data]);

  useEffect(() => {
    if (!(isSystemAdmin || isServiceAdmin)) {
      form.setFieldValue('advisorUserId', currentUser?.id || '');
    }
  }, [advisors, isSystemAdmin, isServiceAdmin]);

  useEffect(() => {
    if (!isAdvisorsLoading && initialValues.advisorUserId) {
      form.setFieldValue('advisorUserId', initialValues.advisorUserId);
    }
  }, [isAdvisorsLoading, initialValues.advisorUserId]);

  if (error instanceof ApiError) {
    return <GenericErrorModal opened onClose={close} />;
  }

  return (
    <Modal opened onClose={close} title="Family details" centered>
      {isLoading ? (
        <PageLoader absolute={false} />
      ) : (
        <form onSubmit={form.onSubmit(values => nextStep({ ...data, ...values }))}>
          <Flex direction="column" gap="md">
            <TextInput
              label="Family name"
              placeholder="Family name"
              {...form.getInputProps('name')}
            />

            {(isSystemAdmin || isServiceAdmin) && (
              <>
                <Divider my="md" color="black" />
                <Select
                  label="Advisor"
                  placeholder="Select advisor"
                  data={advisors.map(advisor => ({
                    value: advisor.id,
                    label: formatName(advisor.firstName, advisor.lastName),
                  }))}
                  {...form.getInputProps('advisorUserId')}
                />
              </>
            )}

            <Group justify="flex-end" mt="md">
              <Button type="submit" variant="filled-pill-black" disabled={!form.isValid()}>
                Next
              </Button>
            </Group>
          </Flex>
        </form>
      )}
    </Modal>
  );
}
